import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, Phone, Mail, Globe, Store } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Page, PageHeader, Empty } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const FIELDS = [
  { k: "item_name", label: "Item name", required: true },
  { k: "supplier_name", label: "Supplier name", required: true },
  { k: "where_to_buy", label: "Where to buy (website, catalogue page or shop)" },
  { k: "phone", label: "Supplier contact number" },
  { k: "email", label: "Supplier email address", type: "email" },
];

function EntryDialog({ entry, onClose, onSaved }) {
  const [f, setF] = useState({ item_name: "", supplier_name: "", where_to_buy: "", phone: "", email: "", notes: "", ...entry });
  const save = async () => {
    try {
      entry.id ? await api.put(`/suppliers/${entry.id}`, f) : await api.post("/suppliers", f);
      toast.success(entry.id ? "Entry updated" : `${f.item_name} logged`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="supplier-dialog">
        <DialogHeader><DialogTitle>{entry.id ? "Edit supplier find" : "Log a hard-to-find item"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {FIELDS.map((fd) => (
            <div key={fd.k} className="space-y-1">
              <Label>{fd.label}{fd.required && " *"}</Label>
              <Input type={fd.type || "text"} value={f[fd.k]} onChange={(e) => setF({ ...f, [fd.k]: e.target.value })} data-testid={`supplier-${fd.k.replace(/_/g, "-")}-input`} />
            </div>
          ))}
          <div className="space-y-1"><Label>Useful notes</Label><Textarea rows={3} value={f.notes} placeholder="Order code, price, lead time, minimum order…" onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="supplier-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.item_name.trim() || !f.supplier_name.trim()} data-testid="supplier-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const linkFor = (w) => (/^https?:\/\//i.test(w) ? w : /^www\./i.test(w) ? `https://${w}` : null);

function EntryCard({ e, canWrite, onEdit, onDelete }) {
  const href = e.where_to_buy && linkFor(e.where_to_buy);
  return (
    <div className="rounded-xl border bg-card p-4 space-y-2 rise" data-testid={`supplier-entry-${e.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold leading-snug" data-testid={`supplier-entry-item-${e.id}`}>{e.item_name}</div>
          <div className="text-sm text-primary font-medium flex items-center gap-1.5 mt-0.5"><Store className="h-3.5 w-3.5" />{e.supplier_name}</div>
        </div>
        {canWrite && (
          <div className="flex">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(e)} data-testid={`supplier-edit-${e.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onDelete(e)} data-testid={`supplier-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        )}
      </div>
      <div className="space-y-1 text-sm">
        {e.where_to_buy && <div className="flex items-start gap-2"><Globe className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />{href ? <a href={href} target="_blank" rel="noreferrer" className="hover:underline break-all">{e.where_to_buy}</a> : <span>{e.where_to_buy}</span>}</div>}
        {e.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /><a href={`tel:${e.phone.replace(/\s/g, "")}`} className="hover:underline">{e.phone}</a></div>}
        {e.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /><a href={`mailto:${e.email}`} className="hover:underline break-all">{e.email}</a></div>}
      </div>
      {e.notes && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{e.notes}</p>}
      <div className="text-[11px] text-muted-foreground pt-1">Logged by {e.added_by}{e.source === "jonny" ? " via Jonny" : ""}{e.updated_by ? ` · edited by ${e.updated_by}` : ""}</div>
    </div>
  );
}

export default function SupplierFinderPage() {
  const { can } = useAuth();
  const canWrite = can("suppliers:write");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/suppliers", { params: { q } }).then((r) => setRows(r.data)), [q]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);
  const remove = async () => {
    try {
      await api.delete(`/suppliers/${del.id}`);
      toast.success("Entry deleted");
      setDel(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Page wide>
      <PageHeader title="Supplier finder" subtitle="Hard-to-find items the department has sourced before, and where to get them.">
        {canWrite && <Button onClick={() => setEdit({})} data-testid="supplier-add-button"><Plus className="h-4 w-4 mr-2" />Log an item</Button>}
      </PageHeader>
      <div className="relative mb-6 max-w-2xl">
        <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items, suppliers or notes — e.g. lithium wire" className="pl-10 h-11 rounded-xl" data-testid="supplier-search-input" />
      </div>
      {rows && <p className="text-xs text-muted-foreground mb-3" data-testid="supplier-result-count">{rows.length} entr{rows.length === 1 ? "y" : "ies"}{q ? ` matching “${q}”` : ""}</p>}
      {rows && rows.length === 0 ? (
        <Empty>{q ? `Nothing logged for “${q}” yet.${canWrite ? " Found it somewhere? Log it so the next person can." : ""}` : "No supplier finds logged yet."}</Empty>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="supplier-list">
          {(rows || []).map((e) => <EntryCard key={e.id} e={e} canWrite={canWrite} onEdit={setEdit} onDelete={setDel} />)}
        </div>
      )}
      {edit && <EntryDialog entry={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this entry?</AlertDialogTitle><AlertDialogDescription>{del?.item_name} from {del?.supplier_name} will be removed for everyone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Keep</AlertDialogCancel><AlertDialogAction onClick={remove} data-testid="supplier-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
